import React, { useState, useEffect, useRef } from "react";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField, { BaseTextFieldProps } from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import ValidationResult from "../../interfaces/api/validation-result";
import { useSharedSelector } from "../../store/SharedProvider";
import { selectIsUserAdminOrHigher } from "../../store/app-configurations/app-configurations-selectors";

interface CustomTextFieldProps extends BaseTextFieldProps {
  value: string | number;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  minValue?: number;
  endAdornment?: React.ReactNode;
  validationResult?: ValidationResult;
  adminOnly?: boolean;
  hideHelpText?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onEndIconClick?: () => void;
}

const TextInput: React.FC<CustomTextFieldProps> = ({
  endIcon,
  startIcon,
  validationResult,
  minValue,
  endAdornment,
  adminOnly,
  disabled,
  hideHelpText,
  onBlur,
  onEndIconClick,
  ...props
}) => {
  const { t } = useTranslation();

  const isAdmin = useSharedSelector((state) => selectIsUserAdminOrHigher(state));

  const [startValue] = useState(props.value);
  const valueRef = useRef(props.value);

  useEffect(() => {
    valueRef.current = props.value;
  }, [props.value])

  const mouseDownEndIconHandler = (
    event: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const onBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
    return (startValue !== valueRef.current && onBlur != null ? onBlur(event) : undefined)
  }

  return (
    <TextField
      {...props}
      fullWidth
      onBlur={onBlurHandler}
      disabled={disabled || (!isAdmin && adminOnly)}
      minRows={props.multiline ? 2 : undefined}
      helperText={
        hideHelpText === true
          ? undefined
          : validationResult !== undefined && validationResult.error !== null
          ? t(validationResult.error)
          : undefined
      }
      type={typeof props.value !== "string" && isNaN(props.value) ? "text" : props.type}
      value={typeof props.value !== "string" && isNaN(props.value) ? "" : props.value}
      InputProps={{
        inputProps: { min: minValue },
        startAdornment:
          startIcon !== undefined ? <InputAdornment position="start">{startIcon}</InputAdornment> : undefined,
        endAdornment:
          endAdornment != null ? (
            endAdornment
          ) : endIcon !== undefined ? (
            <InputAdornment position="end">
              {onEndIconClick !== undefined ? (
                <IconButton onClick={onEndIconClick} onMouseDown={mouseDownEndIconHandler} edge="end">
                  {endIcon}
                </IconButton>
              ) : (
                endIcon
              )}
            </InputAdornment>
          ) : undefined,
      }}
      error={validationResult !== undefined && !validationResult.isValid}
    />
  );
};

export default TextInput;
